import React, { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react';
import { Input, NativeSelect, Spacer, Stack } from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { Context } from '../App';
import { PropertyKey, PropertyValue } from '../model/property';
import { getPropertyKeys } from '../api/property-key-controller';
import { QueryKeys } from '../api/base/react-query';
import { SPACING } from '../generic/config';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import Card from './Card';
import Table, { TableProps } from './Table';
import useDialog from './hooks/useDialog';

type PropertyValuesCardProps = {
    objectType: string;
    objectId: number;
    values: PropertyValue[];
    onChange: (values: PropertyValue[]) => void;
};

type AddPropertyFormHandle = {
    getSelection: () => PropertyValue | null;
};

type AddPropertyFormProps = {
    objectType: string;
    objectId: number;
    existingKeyIds: Set<number>;
};

const AddPropertyForm = forwardRef<AddPropertyFormHandle, AddPropertyFormProps>(
    ({ objectType, objectId, existingKeyIds }, ref) => {
        const { translations } = useContext(Context);
        const t = translations.common.properties;

        const { data: allKeys = [] } = useQuery(
            [QueryKeys.propertyKeys, objectType],
            () => getPropertyKeys(objectType),
        );
        const [selectedKey, setSelectedKey] = useState<PropertyKey | null>(null);
        const [value, setValue] = useState('');

        useImperativeHandle(ref, () => ({
            getSelection: () => selectedKey ? {
                id: selectedKey.id,
                objectType,
                objectId,
                propertyKey: selectedKey,
                value,
            } : null,
        }));

        const handleKeyChange = (keyId: string) => {
            const key = allKeys.find(k => k.id === Number(keyId)) ?? null;
            setSelectedKey(key);
            setValue(key?.propertyType === 'BOOLEAN' ? '0' : '');
        };

        return (
            <Stack gap={SPACING}>
                <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                        value={selectedKey?.id ?? ''}
                        onChange={(e) => handleKeyChange(e.target.value)}
                    >
                        <option value="">{t.selectProperty}</option>
                        {allKeys.map((k: PropertyKey) => (
                            <option key={k.id} value={k.id} disabled={existingKeyIds.has(k.id)}>{k.key}</option>
                        ))}
                    </NativeSelect.Field>
                </NativeSelect.Root>
                {selectedKey?.propertyType === 'BOOLEAN' && (
                    <Switch
                        checked={value === '1'}
                        onChange={(e) => setValue((e.target as HTMLInputElement).checked ? '1' : '0')}
                    />
                )}
                {selectedKey?.propertyType === 'STRING' && (
                    <Input
                        size="sm"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                    />
                )}
            </Stack>
        );
    }
);

const PropertyValuesCard: React.FC<PropertyValuesCardProps> = ({ objectType, objectId, values, onChange }) => {
    const { translations } = useContext(Context);
    const t = translations.common.properties;
    const { openDialog } = useDialog();
    const formRef = useRef<AddPropertyFormHandle>(null);

    const updateValue = (row: PropertyValue, newValue: string) => {
        onChange(values.map(v => v.propertyKey.id === row.propertyKey.id ? { ...v, value: newValue } : v));
    };

    const onAddClick = () => {
        const existingKeyIds = new Set(values.map(v => v.propertyKey.id));
        openDialog({
            title: t.addProperty,
            body: (
                <AddPropertyForm
                    ref={formRef}
                    objectType={objectType}
                    objectId={objectId}
                    existingKeyIds={existingKeyIds}
                />
            ),
            buttons: [{
                title: t.addProperty,
                onClick: () => {
                    const selection = formRef.current?.getSelection();
                    if (selection) onChange([...values, selection]);
                },
            }],
        });
    };

    const tableProps: TableProps<PropertyValue> = {
        columns: [
            {
                title: t.propertyKey,
                scale: 1,
                getter: (row) => row.propertyKey.key,
            },
            {
                title: t.value,
                scale: 2,
                getter: (row) => {
                    if (row.propertyKey.propertyType === 'BOOLEAN') {
                        return (
                            <Switch
                                checked={row.value === '1'}
                                onChange={(e) => updateValue(row, (e.target as HTMLInputElement).checked ? '1' : '0')}
                            />
                        );
                    }
                    return (
                        <Input
                            size="sm"
                            defaultValue={row.value}
                            onBlur={(e) => updateValue(row, e.target.value)}
                        />
                    );
                },
            },
        ],
        rows: values,
        contextMenuActions: [
            {
                title: t.remove,
                onClick: (row) => onChange(values.filter(v => v.propertyKey.id !== row.propertyKey.id)),
            },
        ],
    };

    return (
        <Card title={t.cardTitle} description={t.cardDescription}>
            <Table tableProps={tableProps} />
            <Spacer />
            <Button size="sm" onClick={onAddClick}>
                {t.addProperty}
            </Button>
        </Card>
    );
};

export default PropertyValuesCard;
